/**
 * Shared Framer Motion presets for scroll-triggered animations.
 *
 * Import these instead of writing inline transition objects.
 * Component-specific motion (spring physics, UI toggles, carousel) stays local.
 */

// Base constants — change here to update the whole site
export const MOTION_EASE = "easeOut" as const;
export const MOTION_DURATION = 0.7;

/**
 * Fade + rise on scroll.
 * Use for headings, paragraphs, and content blocks entering the viewport.
 */
export const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 as const },
  transition: { duration: MOTION_DURATION, ease: MOTION_EASE, delay },
});

/**
 * Fade + slide horizontally on scroll.
 * Use for side-by-side content columns.
 */
export const slideIn = (direction: "left" | "right", delay = 0) => ({
  initial: { opacity: 0, x: direction === "left" ? -50 : 50 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 as const },
  transition: { duration: MOTION_DURATION, ease: MOTION_EASE, delay },
});

/**
 * Instant fade for components that should respect prefers-reduced-motion.
 * Use for new UI surfaces (panels, rails, overlays) instead of slide-in presets.
 */
export const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.01 } },
};

/**
 * Orchestrates children entering one-by-one. Spread onto a parent motion element
 * whose children each carry the `staggerItem` variants.
 *
 * @param delayChildren  Wait before the first child animates (e.g. let a sibling
 *                       column slide in first).
 * @param staggerChildren Gap between each child's start.
 */
export const staggerContainer = (delayChildren = 0, staggerChildren = 0.15) => ({
  initial: "hidden" as const,
  whileInView: "visible" as const,
  viewport: { once: true, amount: 0.2 as const },
  variants: {
    hidden: {},
    visible: { transition: { delayChildren, staggerChildren } },
  },
});

/**
 * Fade + rise for a single item inside a `staggerContainer`.
 * Apply via `variants={staggerItem}` on each child motion element.
 */
export const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION_DURATION, ease: MOTION_EASE },
  },
};

/**
 * Full-page scroll section: blur + fade + vertical slide.
 * Used by the ScrollSection wrapper component.
 */
export const scrollSectionProps = {
  initial: { opacity: 0, y: 50, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -50, filter: "blur(10px)" },
  viewport: { once: true, amount: 0.3 as const },
  transition: { duration: 0.8, ease: MOTION_EASE },
};
